import type {
	ConstantsAdminStatsStruct,
	ConstantsAdminTeamDetailStruct,
	ConstantsAdminTeamListStruct,
	ConstantsAdminUserDetailStruct,
	ConstantsAdminUserListStruct,
} from "@gosaas/types";
import { useQuery } from "@tanstack/react-query";
import { apiClient, requestWithHandling } from "@/utils/api";
import { reportQueryError } from "@/utils/query-errors";

export const useFetchAdminStats = () => {
	const {
		data: stats,
		isLoading: isLoadingStats,
		refetch: refetchStats,
	} = useQuery({
		queryKey: ["admin-stats"],
		queryFn: async () => {
			try {
				const response = await requestWithHandling<ConstantsAdminStatsStruct>(
					() => apiClient.api.adminStatsList(),
				);
				if (response.code === "SUCCESS") {
					return response.data;
				}
				throw new Error(response.message || "Failed to fetch stats");
			} catch (err) {
				reportQueryError(err, { title: "Failed to load platform stats" });
			}
		},
		retry: 1,
		retryDelay: 1000,
	});

	return { stats, isLoadingStats, refetchStats };
};

export const useFetchAdminUsers = (query: string, page: number) => {
	const {
		data: userList,
		isLoading: isLoadingUsers,
		refetch: refetchUsers,
	} = useQuery({
		queryKey: ["admin-users", query, page],
		queryFn: async () => {
			try {
				const response =
					await requestWithHandling<ConstantsAdminUserListStruct>(() =>
						apiClient.api.adminUsersList({
							query: query || undefined,
							page,
						}),
					);
				if (response.code === "SUCCESS") {
					return response.data;
				}
				throw new Error(response.message || "Failed to fetch users");
			} catch (err) {
				reportQueryError(err, { title: "Failed to load users" });
			}
		},
		retry: 1,
		retryDelay: 1000,
	});

	return { userList, isLoadingUsers, refetchUsers };
};

export const useFetchAdminUserDetail = (userId: string) => {
	const {
		data: userDetail,
		isLoading: isLoadingUserDetail,
		refetch: refetchUserDetail,
	} = useQuery({
		queryKey: ["admin-user-detail", userId],
		queryFn: async () => {
			try {
				const response =
					await requestWithHandling<ConstantsAdminUserDetailStruct>(() =>
						apiClient.api.adminUsersDetail(userId),
					);
				if (response.code === "SUCCESS") {
					return response.data;
				}
				throw new Error(response.message || "Failed to fetch user");
			} catch (err) {
				reportQueryError(err, { title: "Failed to load user" });
			}
		},
		enabled: !!userId,
		retry: 1,
		retryDelay: 1000,
	});

	return { userDetail, isLoadingUserDetail, refetchUserDetail };
};

export const useFetchAdminTeams = (query: string, page: number) => {
	const {
		data: teamList,
		isLoading: isLoadingTeams,
		refetch: refetchTeams,
	} = useQuery({
		queryKey: ["admin-teams", query, page],
		queryFn: async () => {
			try {
				const response =
					await requestWithHandling<ConstantsAdminTeamListStruct>(() =>
						apiClient.api.adminTeamsList({
							query: query || undefined,
							page,
						}),
					);
				if (response.code === "SUCCESS") {
					return response.data;
				}
				throw new Error(response.message || "Failed to fetch teams");
			} catch (err) {
				reportQueryError(err, { title: "Failed to load teams" });
			}
		},
		retry: 1,
		retryDelay: 1000,
	});

	return { teamList, isLoadingTeams, refetchTeams };
};

export const useFetchAdminTeamDetail = (teamId: string) => {
	const {
		data: teamDetail,
		isLoading: isLoadingTeamDetail,
		refetch: refetchTeamDetail,
	} = useQuery({
		queryKey: ["admin-team-detail", teamId],
		queryFn: async () => {
			try {
				const response =
					await requestWithHandling<ConstantsAdminTeamDetailStruct>(() =>
						apiClient.api.adminTeamsDetail(teamId),
					);
				if (response.code === "SUCCESS") {
					return response.data;
				}
				throw new Error(response.message || "Failed to fetch team");
			} catch (err) {
				reportQueryError(err, { title: "Failed to load team" });
			}
		},
		enabled: !!teamId,
		retry: 1,
		retryDelay: 1000,
	});

	return { teamDetail, isLoadingTeamDetail, refetchTeamDetail };
};
